import {
  Building2,
  Cog,
  Computer,
  EllipsisVertical,
  FileLock,
  LogOut,
  ShieldHalf,
  User,
} from "lucide-react";
import { Link } from "react-router";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { SessionRoleLabels } from "@/data/types";
import {
  COOKIE_POLICY_URL,
  getEnvironmentName,
  PRIVACY_POLICY_URL,
} from "@/lib/env";
import useStore from "@/state";

export const NavUser = () => {
  const { isMobile } = useSidebar();
  const { session, logOut } = useStore();

  if (!session) {
    return null;
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarImage
                  src={session.user.profile_picture_url ?? ""}
                  alt={session.user.first_name ?? ""}
                />
                <AvatarFallback className="rounded-lg">
                  {session.user.first_name?.[0]}
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">
                  {session.user.first_name} {session.user.last_name}
                </span>
                <span className="truncate text-xs">{session.user.email}</span>
              </div>
              <EllipsisVertical className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarImage
                    src={session.user.profile_picture_url ?? undefined}
                    alt={session.user.first_name ?? undefined}
                  />
                  <AvatarFallback className="rounded-lg">
                    {session.user.first_name?.[0]}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">
                    {session.user.first_name} {session.user.last_name}
                  </span>
                  <span className="truncate text-xs">{session.user.email}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem disabled>
                <Computer />
                {getEnvironmentName()}
              </DropdownMenuItem>
              <DropdownMenuItem disabled>
                <User />
                {SessionRoleLabels[session.role]} Role
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <Link to="/account">
                <DropdownMenuItem>
                  <Cog />
                  Account
                </DropdownMenuItem>
              </Link>
              <Link to="/organization">
                <DropdownMenuItem>
                  <Building2 />
                  Organization
                </DropdownMenuItem>
              </Link>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <a
                href={PRIVACY_POLICY_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                <DropdownMenuItem>
                  <ShieldHalf />
                  Privacy Policy
                </DropdownMenuItem>
              </a>
              <a
                href={COOKIE_POLICY_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                <DropdownMenuItem>
                  <FileLock />
                  Cookie Policy
                </DropdownMenuItem>
              </a>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logOut}>
              <LogOut />
              Log Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
};
